import { useEffect, useId, useRef } from 'react'
import IconButton from './IconButton'
import './Modal.css'

export default function Modal({ open, title, onClose, children, className = '', closeLabel = 'Dialog schließen', dismissible = true }) {
  const closeButtonRef = useRef(null)
  const dialogRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return undefined

    const returnFocusTarget = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusTimeout = window.setTimeout(() => {
      const focusTarget = closeButtonRef.current || dialogRef.current?.querySelector('input, button, a[href], select, textarea')
      focusTarget?.focus()
    }, 0)
    const handleDialogKeyDown = (event) => {
      if (dismissible && event.key === 'Escape') {
        onCloseRef.current?.()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = [...dialogRef.current.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')]
      if (!focusable.length) {
        event.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleDialogKeyDown)
    return () => {
      window.clearTimeout(focusTimeout)
      window.removeEventListener('keydown', handleDialogKeyDown)
      window.setTimeout(() => {
        if (returnFocusTarget?.isConnected) returnFocusTarget.focus()
      }, 0)
    }
  }, [dismissible, open])

  if (!open) return null

  return (
    <div className="dialog-backdrop" role="presentation" onClick={dismissible ? onClose : undefined}>
      <div
        ref={dialogRef}
        className={`dialog${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div className="dialog-title" id={titleId}>{title}</div>
          {dismissible && (
            <IconButton
              ref={closeButtonRef}
              icon="close"
              label={closeLabel}
              variant="quiet"
              className="dialog-close"
              onClick={onClose}
            />
          )}
        </div>
        {children}
      </div>
    </div>
  )
}
